// src/store/slices/transformationsSlice.ts
/**
 * Transformations Redux Slice
 * 
 * Manages geometric transformations on the coordinate grid.
 * Phase 5: Transformations
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../types/redux';
import {
  Transformation,
  TransformationType,
  TransformParameters,
  Point2D,
} from '../../types/coordinateGrid';
import { applyTransformation } from '../../lib/coordinateGrid/transformations';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export type TransformStep = 'select-targets' | 'set-parameters' | 'preview' | 'complete';

export interface ActiveTransform {
  type: TransformationType;
  targetIds: string[];
  parameters: Partial<TransformParameters>;
  step: TransformStep;
}

export interface TransformationsState {
  transformations: Transformation[];
  /** Active transformation being configured */
  activeTransform: ActiveTransform | null;
  nextId: number;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: TransformationsState = {
  transformations: [],
  activeTransform: null,
  nextId: 1,
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get vertices from selected shapes/points
 */
function getTargetVertices(
  state: RootState,
  targetIds: string[]
): Point2D[] {
  const vertices: Point2D[] = [];
  
  for (const id of targetIds) {
    // Check if it's a shape
    const shape = state.gridShapes.shapes.find(s => s.id === id);
    if (shape) {
      vertices.push(...shape.vertices);
      continue;
    }
    
    // Check if it's a point
    const point = state.plotPoints.points.find(p => p.id === id);
    if (point) {
      vertices.push({ x: point.x, y: point.y });
      continue;
    }
  }
  
  return vertices;
}

// =============================================================================
// REDUX SLICE
// =============================================================================

export const transformationsSlice = createSlice({
  name: 'transformations',
  initialState,
  reducers: {
    /**
     * Start a new transformation
     */
    startTransformation: (
      state,
      action: PayloadAction<{
        type: TransformationType;
        targetIds?: string[];
      }>
    ) => {
      const { type, targetIds = [] } = action.payload;
      
      // Initialize default parameters based on type
      let parameters: Partial<TransformParameters> = {};
      
      switch (type) {
        case 'translation':
          parameters = { type: 'translation', vector: { x: 0, y: 0 } };
          break;
        case 'reflection':
          parameters = { type: 'reflection', line: { preset: 'x-axis' } };
          break;
        case 'rotation':
          parameters = {
            type: 'rotation',
            centre: { x: 0, y: 0 },
            angle: 90,
            direction: 'ccw',
          };
          break;
        case 'enlargement':
          parameters = {
            type: 'enlargement',
            centre: { x: 0, y: 0 },
            scaleFactor: 2,
          };
          break;
      }
      
      state.activeTransform = {
        type,
        targetIds,
        parameters,
        step: targetIds.length > 0 ? 'set-parameters' : 'select-targets',
      };
    },

    /**
     * Set transformation targets
     */
    setTransformTargets: (state, action: PayloadAction<string[]>) => {
      if (state.activeTransform) {
        state.activeTransform.targetIds = action.payload;
        state.activeTransform.step = 'set-parameters';
      }
    },

    /**
     * Set transformation parameters
     */
    setTransformParameters: (
      state,
      action: PayloadAction<Partial<TransformParameters>>
    ) => {
      if (state.activeTransform) {
        state.activeTransform.parameters = {
          ...state.activeTransform.parameters,
          ...action.payload,
        };
      }
    },

    /**
     * Preview transformation (creates preview transformation)
     */
    previewTransformation: (state) => {
      if (!state.activeTransform || state.activeTransform.targetIds.length === 0) {
        return;
      }
      
      const params = state.activeTransform.parameters as TransformParameters;
      if (!params || !('type' in params)) {
        return;
      }
      
      // Remove existing preview
      state.transformations = state.transformations.filter(t => !t.isPreview);
      
      // Create preview transformation
      const preview: Transformation = {
        id: `preview-${Date.now()}`,
        type: state.activeTransform.type,
        targetIds: [...state.activeTransform.targetIds],
        showOriginal: true,
        showConstruction: true,
        imageColor: '#3B82F6',
        parameters: params,
        isPreview: true,
      };
      
      state.transformations.push(preview);
      state.activeTransform.step = 'preview';
    },

    /**
     * Confirm transformation - creates transformed shapes/points
     * Note: The actual creation of transformed shapes/points should be handled
     * by the component calling this action, as it needs access to the full state
     */
    confirmTransformation: (state) => {
      if (!state.activeTransform || state.activeTransform.targetIds.length === 0) {
        return;
      }
      
      const params = state.activeTransform.parameters as TransformParameters;
      if (!params || !('type' in params)) {
        return;
      }
      
      // Remove preview
      state.transformations = state.transformations.filter(t => !t.isPreview);
      
      // Create actual transformation record
      const transformation: Transformation = {
        id: `transform-${state.nextId}`,
        type: state.activeTransform.type,
        targetIds: [...state.activeTransform.targetIds],
        showOriginal: true,
        showConstruction: true,
        imageColor: '#10B981',
        parameters: params,
        isPreview: false,
      };
      
      state.transformations.push(transformation);
      state.nextId++;
      
      // Reset active transform
      state.activeTransform = null;
    },

    /**
     * Cancel active transformation
     */
    cancelTransformation: (state) => {
      // Remove preview transformations
      state.transformations = state.transformations.filter(t => !t.isPreview);
      state.activeTransform = null;
    },

    /**
     * Update an existing transformation
     */
    updateTransformation: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<Transformation>;
      }>
    ) => {
      const transformation = state.transformations.find(
        t => t.id === action.payload.id
      );
      if (transformation) {
        Object.assign(transformation, action.payload.updates);
      }
    },

    /**
     * Delete a transformation
     */
    deleteTransformation: (state, action: PayloadAction<string>) => {
      state.transformations = state.transformations.filter(
        t => t.id !== action.payload
      );
    },

    /**
     * Toggle show original for a transformation
     */
    toggleShowOriginal: (state, action: PayloadAction<string>) => {
      const transformation = state.transformations.find(
        t => t.id === action.payload
      );
      if (transformation) {
        transformation.showOriginal = !transformation.showOriginal;
      }
    },

    /**
     * Toggle show construction aids for a transformation
     */
    toggleShowConstruction: (state, action: PayloadAction<string>) => {
      const transformation = state.transformations.find(
        t => t.id === action.payload
      );
      if (transformation) {
        transformation.showConstruction = !transformation.showConstruction;
      }
    },

    /**
     * Load transformations state (for scene loading)
     */
    loadTransformationsState: (
      state,
      action: PayloadAction<Partial<TransformationsState>>
    ) => {
      Object.assign(state, { ...initialState, ...action.payload });
    },

    /**
     * Clear all transformations
     */
    clearAllTransformations: (state) => {
      state.transformations = [];
      state.activeTransform = null;
    },
  },
});

// Export actions
export const {
  startTransformation,
  setTransformTargets,
  setTransformParameters,
  previewTransformation,
  confirmTransformation,
  cancelTransformation,
  updateTransformation,
  deleteTransformation,
  toggleShowOriginal,
  toggleShowConstruction,
  loadTransformationsState,
  clearAllTransformations,
} = transformationsSlice.actions;

// Selectors
export const selectAllTransformations = (state: RootState) =>
  state.transformations.transformations;
export const selectActiveTransform = (state: RootState) =>
  state.transformations.activeTransform;
export const selectTransformationById = (id: string) => (state: RootState) =>
  state.transformations.transformations.find(t => t.id === id);
export const selectPreviewTransformations = (state: RootState) =>
  state.transformations.transformations.filter(t => t.isPreview);

export default transformationsSlice.reducer;
